import { NavLink } from "react-router-dom";
import { useClerk, useUser } from "@clerk/clerk-react";
import { 
  LayoutDashboard, 
  Building2, 
  BarChart3, 
  GitCompare,
  Workflow,
  MessageSquareText,
  FileText,
  LogOut,
  User,
  Globe2,
  GitBranch,
  Calendar,
  Scale,
  BookOpen,
  HelpCircle,
  ChevronRight,
  Users
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Logo URL - Replace with your own hosted logo for production
// The emergentagent.com URL only works in preview, not on custom domains
const LOGO_URL = "/logo.png";

const navItems = [
  {
    section: "Overview",
    items: [
      { path: "/", icon: LayoutDashboard, label: "Dashboard" },
      { path: "/platforms", icon: Building2, label: "Platform Profiles" },
      { path: "/countries", icon: Globe2, label: "Country Compliance" },
      { path: "/country-comparison", icon: Scale, label: "Country Comparison" },
      { path: "/deadlines", icon: Calendar, label: "Deadlines" },
    ]
  },
  {
    section: "Analysis",
    items: [
      { path: "/gap-analysis", icon: BarChart3, label: "Gap Analysis" },
      { path: "/comparison", icon: GitCompare, label: "Compliance Comparison" },
      { path: "/compliance-models", icon: Workflow, label: "Service Models" },
      { path: "/business-flows", icon: GitBranch, label: "Business Flows" },
    ]
  },
  {
    section: "Tools",
    items: [
      { path: "/knowledge-base", icon: BookOpen, label: "Knowledge Base" },
      { path: "/competitors", icon: Users, label: "Competitors" },
      { path: "/pitch-generator", icon: MessageSquareText, label: "Pitch Generator" },
      { path: "/reports", icon: FileText, label: "Reports" },
    ]
  }
];

export default function Sidebar() {
  const { signOut, openUserProfile } = useClerk();
  const { user } = useUser();

  const handleSignOut = () => {
    signOut({ redirectUrl: '/sign-in' });
  };

  return (
    <aside className="sidebar" data-testid="sidebar">
      {/* Logo */}
      <div className="sidebar-logo">
        <img src={LOGO_URL} alt="Mind Your Waste" data-testid="logo" />
      </div>
      
      {/* Navigation */}
      <nav className="sidebar-nav sidebar-scroll">
        {navItems.map((section) => (
          <div key={section.section} className="nav-section">
            <div className="nav-section-title">{section.section}</div>
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => 
                  `nav-link ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                end={item.path === "/"}
              >
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      
      {/* Bottom Section */}
      <div className="sidebar-bottom">
        {/* Help Link */}
        <button className="sidebar-bottom-link group">
          <HelpCircle className="w-4 h-4" />
          <span>Help & Support</span>
          <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
        
        {/* User Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="sidebar-user"
              data-testid="user-menu-trigger"
            >
              <div className="sidebar-user-avatar">
                {user?.imageUrl ? (
                  <img 
                    src={user.imageUrl} 
                    alt={user?.firstName || 'User'} 
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  user?.firstName?.charAt(0) || user?.emailAddresses?.[0]?.emailAddress?.charAt(0)?.toUpperCase() || 'U'
                )}
              </div>
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">
                  {user?.firstName || user?.emailAddresses?.[0]?.emailAddress?.split('@')[0] || 'User'}
                </span>
                <span className="text-[10px] text-gray-500">Pro Account</span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56">
            <DropdownMenuItem 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => openUserProfile()}
              data-testid="profile-settings-btn"
            >
              <User size={16} />
              <span>Profile Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="flex items-center gap-2 text-red-500"
              onClick={handleSignOut}
              data-testid="sign-out-btn"
            >
              <LogOut size={16} />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
